import { FC, lazy, Suspense } from "react";
import { Route, Routes } from "react-router-dom";

import LayoutComponent from "../component/Layout";

import ErrorBoundary from "../component/common/Errors/ErrorBoundary";
import NotFound from "../component/common/Errors/NotFound";
import LoadingIndicator from "../component/common/LoadingIndicator/LoadingIndicator";
import { ROUTES } from "../constants/routes";
import { isLoggedIn } from "../utils/utility";
import ProtectedRoute from "./ProtectedRoute";
import { useAuth } from "../context/auth-context";

const LoginPage = lazy(() => import("../pages/Login/Login.page"));
const ProfilePage = lazy(() => import("../pages/Profile/Profile.page"));
const DashboardPage = lazy(() => import("../pages/Dashboard/Dashboard.page"));
const AgentPage = lazy(() => import("../pages/Agent/Agent.page"));
const BusPage = lazy(() => import("../pages/Bus/Bus.page"));
const DatabasePage = lazy(() => import("../pages/Database/Database.page"));
const TripPage = lazy(() => import("../pages/Trip/Trip.page"));
const UserPage = lazy(() => import("../pages/User/User.page"));
const TicketPage = lazy(() => import("../pages/Ticket/Ticket.page"));
const AuditPage = lazy(() => import("../pages/Audit/Audit.page"));
const ReportPage = lazy(() => import("../pages/Report/Report.page"));
const OfechoReportPage = lazy(
  () => import("../pages/External/OfechoReport.page")
);
const TicketRulesRegulationsPage = lazy(
  () => import("../pages/TicketRulesRegulations")
);
const ERCAPage = lazy(() => import("../pages/ERCA/ERCA.page"));
const TicketPrintPage = lazy(() => import("../pages/TicketPrint"));
const AccessControlPage = lazy(
  () => import("../pages/AccessControl/AccessControl.page")
);
const B2BPage = lazy(() => import("../pages/B2B/B2b.page"));
const B2BReceiptPrint = lazy(() => import("../component/reciept"));
const ERPIntegrationPage = lazy(
  () => import("../pages/ERPIntegration/ERPIntegration.page")
);
const MOLTIntegrationPage = lazy(
  () => import("../pages/MOLTIntegration/MOLTIntegration.page")
);
const Router: FC<{}> = ({}) => {
  const { hasPermission } = useAuth();
  return (
    <Routes>
      <Route
        path={"/abc_ofecho/:token"}
        errorElement={<ErrorBoundary />}
        element={
          <Suspense fallback={<LoadingIndicator />}>
            <OfechoReportPage />
          </Suspense>
        }
      />

      <Route
        path={ROUTES.LOGIN}
        errorElement={<ErrorBoundary />}
        element={
          <Suspense fallback={<LoadingIndicator />}>
            <LoginPage />
          </Suspense>
        }
      />

      <Route
        path={ROUTES.ROOT}
        errorElement={<ErrorBoundary />}
        element={
          <ProtectedRoute>
            <LayoutComponent>
              <Suspense fallback={<LoadingIndicator />}>
                <DashboardPage />
              </Suspense>
            </LayoutComponent>
          </ProtectedRoute>
        }
      />

      <Route
        path={ROUTES.PROFILE}
        errorElement={<ErrorBoundary />}
        element={
          <ProtectedRoute>
            <LayoutComponent>
              <Suspense fallback={<LoadingIndicator />}>
                <ProfilePage />
              </Suspense>
            </LayoutComponent>
          </ProtectedRoute>
        }
      />

      <Route
        path={ROUTES.AGENT}
        errorElement={<ErrorBoundary />}
        element={
          <ProtectedRoute>
            <LayoutComponent>
              <Suspense fallback={<LoadingIndicator />}>
                <AgentPage />
              </Suspense>
            </LayoutComponent>
          </ProtectedRoute>
        }
      />

      <Route
        path={ROUTES.AGENT_TAB}
        errorElement={<ErrorBoundary />}
        element={
          <ProtectedRoute>
            <LayoutComponent>
              <Suspense fallback={<LoadingIndicator />}>
                <AgentPage />
              </Suspense>
            </LayoutComponent>
          </ProtectedRoute>
        }
      />

      <Route
        path={ROUTES.DATABASE}
        errorElement={<ErrorBoundary />}
        element={
          <ProtectedRoute>
            <LayoutComponent>
              <Suspense fallback={<LoadingIndicator />}>
                <DatabasePage />
              </Suspense>
            </LayoutComponent>
          </ProtectedRoute>
        }
      />

      <Route
        path={ROUTES.DATABASE_TAB}
        errorElement={<ErrorBoundary />}
        element={
          <ProtectedRoute>
            <LayoutComponent>
              <Suspense fallback={<LoadingIndicator />}>
                <DatabasePage />
              </Suspense>
            </LayoutComponent>
          </ProtectedRoute>
        }
      />

      <Route
        path={ROUTES.BUS}
        errorElement={<ErrorBoundary />}
        element={
          <ProtectedRoute>
            <LayoutComponent>
              <Suspense fallback={<LoadingIndicator />}>
                <BusPage />
              </Suspense>
            </LayoutComponent>
          </ProtectedRoute>
        }
      />

      <Route
        path={ROUTES.BUS_TAB}
        errorElement={<ErrorBoundary />}
        element={
          <ProtectedRoute>
            <LayoutComponent>
              <Suspense fallback={<LoadingIndicator />}>
                <BusPage />
              </Suspense>
            </LayoutComponent>
          </ProtectedRoute>
        }
      />

      <Route
        path={ROUTES.TRIP}
        errorElement={<ErrorBoundary />}
        element={
          <ProtectedRoute>
            <LayoutComponent>
              <Suspense fallback={<LoadingIndicator />}>
                <TripPage />
              </Suspense>
            </LayoutComponent>
          </ProtectedRoute>
        }
      />

      <Route
        path={ROUTES.TRIP_TAB}
        errorElement={<ErrorBoundary />}
        element={
          <ProtectedRoute>
            <LayoutComponent>
              <Suspense fallback={<LoadingIndicator />}>
                <TripPage />
              </Suspense>
            </LayoutComponent>
          </ProtectedRoute>
        }
      />

      <Route
        path={ROUTES.USER}
        errorElement={<ErrorBoundary />}
        element={
          <ProtectedRoute>
            <LayoutComponent>
              <Suspense fallback={<LoadingIndicator />}>
                <UserPage />
              </Suspense>
            </LayoutComponent>
          </ProtectedRoute>
        }
      />

      <Route
        path={ROUTES.USER_TAB}
        errorElement={<ErrorBoundary />}
        element={
          <ProtectedRoute>
            <LayoutComponent>
              <Suspense fallback={<LoadingIndicator />}>
                <UserPage />
              </Suspense>
            </LayoutComponent>
          </ProtectedRoute>
        }
      />

      <Route
        path={ROUTES.TICKET}
        errorElement={<ErrorBoundary />}
        element={
          <ProtectedRoute>
            <LayoutComponent>
              <Suspense fallback={<LoadingIndicator />}>
                <TicketPage />
              </Suspense>
            </LayoutComponent>
          </ProtectedRoute>
        }
      />

      <Route
        path={ROUTES.TICKET_TAB}
        errorElement={<ErrorBoundary />}
        element={
          <ProtectedRoute>
            <LayoutComponent>
              <Suspense fallback={<LoadingIndicator />}>
                <TicketPage />
              </Suspense>
            </LayoutComponent>
          </ProtectedRoute>
        }
      />

      <Route
        path={ROUTES.AUDIT}
        errorElement={<ErrorBoundary />}
        element={
          <ProtectedRoute>
            <LayoutComponent>
              <Suspense fallback={<LoadingIndicator />}>
                <AuditPage />
              </Suspense>
            </LayoutComponent>
          </ProtectedRoute>
        }
      />

      <Route
        path={ROUTES.AUDIT_TAB}
        errorElement={<ErrorBoundary />}
        element={
          <ProtectedRoute>
            <LayoutComponent>
              <Suspense fallback={<LoadingIndicator />}>
                <AuditPage />
              </Suspense>
            </LayoutComponent>
          </ProtectedRoute>
        }
      />

      <Route
        path={ROUTES.REPORT}
        errorElement={<ErrorBoundary />}
        element={
          <ProtectedRoute>
            <LayoutComponent>
              <Suspense fallback={<LoadingIndicator />}>
                <ReportPage />
              </Suspense>
            </LayoutComponent>
          </ProtectedRoute>
        }
      />

      <Route
        path={ROUTES.REPORT_TAB}
        errorElement={<ErrorBoundary />}
        element={
          <ProtectedRoute>
            <LayoutComponent>
              <Suspense fallback={<LoadingIndicator />}>
                <ReportPage />
              </Suspense>
            </LayoutComponent>
          </ProtectedRoute>
        }
      />

      <Route
        path={ROUTES.ERCA}
        errorElement={<ErrorBoundary />}
        element={
          <ProtectedRoute>
            <LayoutComponent>
              <Suspense fallback={<LoadingIndicator />}>
                <ERCAPage />
              </Suspense>
            </LayoutComponent>
          </ProtectedRoute>
        }
      />

      <Route
        path={ROUTES.ERCA_TAB}
        errorElement={<ErrorBoundary />}
        element={
          <ProtectedRoute>
            <LayoutComponent>
              <Suspense fallback={<LoadingIndicator />}>
                <ERCAPage />
              </Suspense>
            </LayoutComponent>
          </ProtectedRoute>
        }
      />

      <Route
        path={ROUTES.TICKET_RULE_REGULATIONS}
        errorElement={<ErrorBoundary />}
        element={
          <Suspense fallback={<LoadingIndicator />}>
            <TicketRulesRegulationsPage />
          </Suspense>
        }
      />

      <Route
        path={ROUTES.TICKET_PRINT}
        errorElement={<ErrorBoundary />}
        element={
          <Suspense fallback={<LoadingIndicator />}>
            <TicketPrintPage />
          </Suspense>
        }
      />

      <Route
        path={ROUTES.ACCESS_CONTROL}
        errorElement={<ErrorBoundary />}
        element={
          <ProtectedRoute>
            <LayoutComponent>
              <Suspense fallback={<LoadingIndicator />}>
                <AccessControlPage />
              </Suspense>
            </LayoutComponent>
          </ProtectedRoute>
        }
      />

      <Route
        path={ROUTES.ACCESS_CONTROL_TAB}
        errorElement={<ErrorBoundary />}
        element={
          <ProtectedRoute>
            <LayoutComponent>
              <Suspense fallback={<LoadingIndicator />}>
                <AccessControlPage />
              </Suspense>
            </LayoutComponent>
          </ProtectedRoute>
        }
      />

      {hasPermission("erca", "erca:view-all") && (
        <>
          <Route
            path={ROUTES.BUS_RENTAL}
            errorElement={<ErrorBoundary />}
            element={
              <ProtectedRoute>
                <LayoutComponent>
                  <Suspense fallback={<LoadingIndicator />}>
                    <B2BPage />
                  </Suspense>
                </LayoutComponent>
              </ProtectedRoute>
            }
          />

          <Route
            path={ROUTES.BUS_RENTAL_TAB}
            errorElement={<ErrorBoundary />}
            element={
              <ProtectedRoute>
                <LayoutComponent>
                  <Suspense fallback={<LoadingIndicator />}>
                    <B2BPage />
                  </Suspense>
                </LayoutComponent>
              </ProtectedRoute>
            }
          />

          <Route
            path={ROUTES.B2b_BOOKING_PRINT}
            errorElement={<ErrorBoundary />}
            element={
              <Suspense fallback={<LoadingIndicator />}>
                <B2BReceiptPrint />
              </Suspense>
            }
          />
        </>
      )}

      {/* ERP Integration - accessible to all authenticated users */}
      <Route
        path={ROUTES.ERP_INTEGRATION}
        errorElement={<ErrorBoundary />}
        element={
          <ProtectedRoute>
            <LayoutComponent>
              <Suspense fallback={<LoadingIndicator />}>
                <ERPIntegrationPage />
              </Suspense>
            </LayoutComponent>
          </ProtectedRoute>
        }
      />

      {/* MOLT Integration - accessible to all authenticated users */}
      <Route
        path={ROUTES.MOLT_INTEGRATION}
        errorElement={<ErrorBoundary />}
        element={
          <ProtectedRoute>
            <LayoutComponent>
              <Suspense fallback={<LoadingIndicator />}>
                <MOLTIntegrationPage />
              </Suspense>
            </LayoutComponent>
          </ProtectedRoute>
        }
      />

      {/* Handle Undefined path requests - MUST BE LAST */}
      <Route
        path="*"
        element={
          isLoggedIn() ? (
            <LayoutComponent>
              <NotFound />
            </LayoutComponent>
          ) : (
            <NotFound />
          )
        }
      />
    </Routes>
  );
};

export default Router;
